import { Directive, EventEmitter, Input, OnChanges, Output, SimpleChange } from "@angular/core";
import { coerceBoolean } from "@obg/ngx.global.evo";
import { difference, isNil } from "lodash-es";

import { AccordionGroup, AccordionOptions, AccordionToggleChangeArgs } from "./accordion.model";

@Directive({
	selector: "obg-accordion, [obgAccordion]",
})
export class AccordionDirective implements OnChanges {
	@Input()
	@coerceBoolean()
	disabled = false;

	@Input() expanded?: string[];
	@Input() options?: AccordionOptions;
	@Input()
	@coerceBoolean()
	keepRemovedKeysExpanded = false;
	@Input()
	@coerceBoolean()
	animationEnabled = false;

	shouldAnimate = false;

	@Output() toggleChange = new EventEmitter<AccordionToggleChangeArgs>();

	private expandedSet = new Set<string>();
	private groups: Record<string, AccordionGroup> = {};

	ngOnChanges(changes: { expanded?: SimpleChange }) {
		const expanded = changes.expanded;

		if (!expanded) {
			return;
		}

		this.shouldAnimate = this.animationEnabled;

		if (expanded.isFirstChange()) {
			this.expandedSet = new Set<string>(expanded.currentValue);
			return;
		}

		this.closeRemoveExpandedItems(expanded);
		this.openNewExpandedItems(expanded);
	}

	toggle(key: string) {
		this.shouldAnimate = true;
		if (this.disabled) {
			return;
		}

		if (this.expandedSet.has(key)) {
			this.close(key);
			return;
		}

		if (this.options && this.options.collapseOthers) {
			this.closeAll();
		}

		this.open(key);
	}

	open(key: string) {
		this.toggleHandler(key, true);
		this.expandedSet.add(key);
	}

	close(key: string) {
		this.toggleHandler(key, false);
		this.expandedSet.delete(key);
	}

	closeAll() {
		this.expandedSet.forEach((value) => {
			this.close(value);
		});
	}

	register(key: string, group: AccordionGroup) {
		const accordionGroups = this.groups[key];

		if (!accordionGroups) {
			this.groups[key] = group;
			return;
		}

		if (group.header) {
			accordionGroups.header = group.header;
		}

		if (group.content) {
			accordionGroups.content = group.content;
		}

		if (accordionGroups.header && accordionGroups.content) {
			this.toggleHandler(key, this.expandedSet.has(key));
		}
	}

	unregister(key: string) {
		if (!this.keepRemovedKeysExpanded) {
			this.expandedSet.delete(key);
		}
		delete this.groups[key];
	}

	get(key: string): AccordionGroup {
		return this.groups[key];
	}

	private closeRemoveExpandedItems(expanded: SimpleChange) {
		const removeExpandedItems: string[] = difference(expanded.previousValue, expanded.currentValue);
		removeExpandedItems.forEach((key) => this.close(key));
	}

	private openNewExpandedItems(expanded: SimpleChange) {
		const newExpandedItems: string[] = difference(expanded.currentValue, expanded.previousValue);
		newExpandedItems.forEach((key) => this.open(key));
	}

	private toggleHandler(key: string, expand: boolean) {
		const accordion = this.groups[key];
		if (
			!accordion ||
			!accordion.header ||
			!accordion.content ||
			accordion.isExpanded === expand ||
			accordion.header.key === undefined
		) {
			return;
		}

		accordion.header.$$toggleHandler(expand, this.shouldAnimate);
		accordion.content.$$toggleHandler(expand, this.shouldAnimate);

		if (!isNil(accordion.isExpanded)) {
			// we don't want to emit on init
			this.toggleChange.emit({
				key: accordion.header.key,
				isExpanded: expand,
			});
		}

		accordion.isExpanded = expand;
	}
}
